<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="onSave()"
      @close="onCancel()"
      size="sm-md"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
        >
          <h5 class="modal-title">
            New Statement
          </h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="onCancel()"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div
          class="b-row d-flex justify-content-between align-items-center w-100"
        >
          <div class="b-col">
            <span class="text-danger text-sm-left lower-font-size">{{
              error
            }}</span>
          </div>
          <div class="b-col text-right mt-1 mr-2">
            <b-button @click="onCancel()" variant="secondary">Cancel</b-button>
            <b-button class="ml-2" variant="primary" @click="onSave()"
              >Create Statement
            </b-button>
          </div>
        </div>
      </template>

      <div class="position-relative">
        <div class="d-flex">
          <div class="client-form-container position-relative w-100 bg-white">
            <div class="section-body">
              <b-row>
                <b-col class="modal-col">
                  <b-form-group label="Select Patient" class="w-100">
                    <h6 class="mb-0">
                      <eoc-picker
                        :eocs="eocOptions"
                        :async="true"
                        @search="
                          ({ search, loading }) =>
                            fetchEocsOptionsPicker(search, loading)
                        "
                        v-model="patient"
                      />
                    </h6>
                  </b-form-group>
                  <b-form-group label="Date Range" class="w-100">
                    <h6 class="mb-0 d-flex w-100">
                      <div class="mr-4 w-50">
                        <span class="lower-font-size pb-0">From</span>
                        <date-picker
                          inputWidth="120"
                          v-model="dateRange.from"
                        />
                      </div>
                      <div class="w-50">
                        <span class="lower-font-size pb-0">To</span>
                        <date-picker inputWidth="120" v-model="dateRange.to" />
                      </div>
                    </h6>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EocPicker from "../../controls/EocPicker.vue";
import eocServer from "../../../server/eoc";
import DatePicker from "../../controls/DatePicker.vue";

export default {
  name: "BillingDocumentNewStatement",
  components: {
    EocPicker,
    DatePicker
  },
  props: ["showing"],
  data() {
    return {
      patient: null,
      eocOptions: [],
      dateRange: {
        from: null,
        to: null
      },
      error: ""
    };
  },
  async mounted() {
    this.resetValues();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.eocOptions = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          if (search.length >= 3) {
            loading(true);
          }

          this.searchingEocs = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.eocOptions = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));
          this.searchingEocs = false;
          if (search.length >= 3) {
            loading(false);
          }
        }
      }, 1000);
    },
    async fetchEocs() {
      const optionsResult = await eocServer.queryList(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locationId: [],
          allLocations: true,
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.eocOptions = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    resetValues() {
      this.error = "";
      const currentDate = new Date();
      this.dateRange.to = this.formatDateOnly(currentDate);
      this.dateRange.from = this.formatDateOnly(
        currentDate.setMonth(currentDate.getMonth() - 1)
      );
      this.patient = null;
    },
    validateForm() {
      if (this.patient == null) {
        this.error = "You must select a patient!";
        return false;
      }
      if(!this.isValidDateRange()) {
        this.error = "Incorrect date range!";
        return false;
      }
      return true;
    },
    isValidDateRange() {
      const from = Date.parse(this.dateRange.from);
      const to = Date.parse(this.dateRange.to);
      return from < to;
    },
    async onSave() {
      if (this.validateForm()) {
        this.$emit(
          "createNewStatement",
          this.patient.value,
          this.dateRange.from,
          this.dateRange.to
        );
        this.resetValues();
      }
    },
    onCancel() {
      this.resetValues();
      this.$emit("cancelNewStatement");
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    showing: async function(value) {
      if (value) {
        await this.fetchEocs();
      }
    }
  }
};
</script>

<style scoped>
.section-body {
  min-height: 70px;
}

.modal-col {
  margin: 20px;
}

.badge-fixed-size {
  min-width: 101px;
  margin: 0 10px;
}

.add-button {
  max-height: 35px;
  margin: 30px 10px;
}
</style>
